import { ChevronRight, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { HomePageFourPillars } from "../../services/HomeService";
import { hasFourPillarsData } from "../../utils/fourPillarsMapper";
import {
  FOUR_PILLARS,
  getLowestPillarKey,
} from "../../utils/fourPillarsConfig";
import { getPillarScoreStatus } from "../../utils/fourPillarsStatus";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import {
  homeAccentLink,
  homeLowestBadge,
  homeLowestHighlight,
} from "../../utils/homeBlueTheme";
import { cn } from "../../lib/utils";
import { Card } from "../ui/card";

type FourPillarsSectionProps = {
  pillars?: HomePageFourPillars;
  className?: string;
  /** When true, renders without outer card shell (for combined BBS + pillars card). */
  embedded?: boolean;
};

function PillarRow({
  label,
  score,
  icon: Icon,
  iconBg,
  iconColor,
  isLowest,
}: {
  label: string;
  score: number;
  icon: (typeof FOUR_PILLARS)[number]["icon"];
  iconBg: string;
  iconColor: string;
  isLowest: boolean;
}) {
  const status = getPillarScoreStatus(score);
  const progress = Math.max(0, Math.min(100, score));

  return (
    <div
      className={cn(
        "rounded-xl px-3 py-2.5 transition-colors",
        isLowest && homeLowestHighlight,
      )}
    >
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/40",
            iconBg,
          )}
        >
          <Icon className={cn("h-4 w-4", iconColor)} strokeWidth={2.25} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm font-semibold text-foreground">
                {label}
              </span>
              {isLowest ? (
                <span className={cn("shrink-0 rounded-full px-2 py-0.5", homeLowestBadge)}>
                  Lowest
                </span>
              ) : null}
            </div>
            <span
              className={cn(
                "shrink-0 text-sm font-semibold",
                isLowest ? status.textClass : "text-[#737DFE]",
              )}
            >
              {status.label}
            </span>
          </div>

          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted/80">
            <div
              className={cn("h-full rounded-full transition-all duration-700", status.barClass)}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const FourPillarsSection = ({
  pillars,
  className,
  embedded = false,
}: FourPillarsSectionProps) => {
  const navigate = useNavigate();
  // Collapsed by default — show only the lowest pillar as a preview bar.
  const [expanded, setExpanded] = useState(false);

  if (!hasFourPillarsData(pillars)) return null;

  const lowestKey = getLowestPillarKey(pillars);
  const previewKey = lowestKey ?? FOUR_PILLARS[0].key;
  const visiblePillars = expanded
    ? FOUR_PILLARS
    : FOUR_PILLARS.filter((p) => p.key === previewKey);
  const hiddenCount = FOUR_PILLARS.length - visiblePillars.length;

  const handleOpenDetails = () => {
    navigate("/glucera" + GluceraNavigationRoutes.FOUR_PILLARS_DETAIL);
  };

  const pillarsBody = (
    <div className={cn("space-y-3", embedded ? "p-4 pt-3" : "p-4")}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-bold tracking-tight text-foreground">
          Your 4 pillars
        </h3>
        <button
          type="button"
          onClick={handleOpenDetails}
          className={cn("inline-flex shrink-0 items-center gap-0.5 text-sm font-medium", homeAccentLink)}
        >
          Details
          <ChevronRight className="h-4 w-4" strokeWidth={2.25} aria-hidden />
        </button>
      </div>

      <div className="space-y-1">
        {visiblePillars.map((pillar) => (
          <PillarRow
            key={pillar.key}
            label={pillar.label}
            score={pillars[pillar.key]}
            icon={pillar.icon}
            iconBg={pillar.iconBg}
            iconColor={pillar.iconColor}
            isLowest={pillar.key === lowestKey}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className={cn("flex w-full items-center justify-center gap-1 rounded-lg py-1.5 text-compact-sm font-semibold transition-colors hover:bg-muted/40", homeAccentLink)}
      >
        {expanded ? "See less" : `See more (${hiddenCount})`}
        <ChevronDown
          className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")}
          aria-hidden
        />
      </button>
    </div>
  );

  if (embedded) {
    return (
      <div className={className} aria-label="Your four pillars">
        {pillarsBody}
      </div>
    );
  }

  return (
    <Card
      className={cn(
        "overflow-hidden rounded-2xl border border-border/65 bg-card shadow-sm",
        className,
      )}
      aria-label="Your four pillars"
    >
      {pillarsBody}
    </Card>
  );
};

export default FourPillarsSection;
